import { useState } from 'react';
import { performAuthorizationCodeFlow } from '../oauth-provider';
import type { OAuthOpenResponse } from '../oauth-provider';
import type { OauthProvidersConfig } from '../types';
import { getPrimaryConnectProviderConfig } from '../utils';
import { useSalesforceEcaMigrationEnabled } from '../salesforce/use-salesforce-eca-migration-enabled';

type UseOAuthUpgradeArgs = {
  /** Option ID for the entity (e.g. `'salesforce'`). Threaded into the popup-window title. */
  optionId: string;
  /** Fetched provider config (e.g. `useGetSourceOAuthProviderConfig` / `useGetDestinationOAuthProviderConfig`). */
  providerConfig: { data: OauthProvidersConfig | undefined; isLoading: boolean };
  /** Persist any in-flight setting changes to the entity (called before the OAuth connection is created). */
  updateEntity: () => Promise<unknown>;
  /** Create the OAuth connection from the authorization-code response. */
  createConnection: (response: OAuthOpenResponse) => Promise<unknown>;
  /** Surface failures (missing provider config or authorize-flow errors) to the user. */
  onError: (error: unknown) => void;
};

/**
 * Orchestrates the "upgrade my OAuth connection" flow shared by sources and
 * destinations:
 *   1. pick the right provider (legacy or `salesforce_eca` based on the
 *      `cdp_salesforce_eca_migration` flag),
 *   2. run the browser-side authorization-code flow,
 *   3. persist the entity (so any settings edits are saved before the
 *      backend resolves the new connection), and
 *   4. create the connection record from the authorization response.
 *
 * Guards against re-entrancy and surfaces failures via the injected `onError`
 * callback — callers wire this to their app-level error reporter
 * (e.g. `notifyOnError`) so the hook itself stays free of that dependency.
 */
export function useOAuthUpgrade({
  optionId,
  providerConfig,
  updateEntity,
  createConnection,
  onError,
}: UseOAuthUpgradeArgs) {
  const [isUpgrading, setIsUpgrading] = useState(false);
  const isSalesforceEcaMigrationEnabled = useSalesforceEcaMigrationEnabled();
  const providers = providerConfig.data?.providers ?? [];

  const upgrade = async () => {
    if (isUpgrading) return;
    setIsUpgrading(true);
    const cfg = getPrimaryConnectProviderConfig(optionId, providers, isSalesforceEcaMigrationEnabled);
    if (cfg == null) {
      onError(new Error(`OAuth provider configuration for ${optionId} not available`));
      setIsUpgrading(false);
      return;
    }
    try {
      const response = await performAuthorizationCodeFlow({
        name: optionId,
        oauthKeyName: cfg.oauth_key_name,
        authorizeUrl: cfg.authorize_url,
        clientId: cfg.client_id,
        scope: cfg.scope ?? '',
        usePkce: cfg.use_pkce,
        codeChallengeMethod: cfg.code_challenge_method,
      });
      await updateEntity();
      await createConnection(response);
    } catch (error) {
      onError(error);
    } finally {
      setIsUpgrading(false);
    }
  };

  return { upgrade, isUpgrading, isLoading: providerConfig.isLoading };
}
